import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { MODULES_DEEP, TONES, PIPELINE, CHART_BARS } from './homeData';

// ─── Small shared primitives for the live module screens ─────────────────────
const KpiTile = ({ label, value, tone, sub }) => {
  const t = tone ? TONES[tone] : null;
  return (
    <div className="mr-kpi" style={t ? { background: t[1], borderColor: 'transparent' } : undefined}>
      <div className="mr-kpi-label" style={t ? { color: t[0] } : undefined}>{label}</div>
      <div className="mr-kpi-value" style={t ? { color: t[0] } : undefined}>{value}</div>
      {sub}
    </div>
  );
};

const SubBadge = ({ text, tone }) => {
  const t = TONES[tone];
  return <span className="mr-subbadge" style={{ color: t[0], background: t[1] }}>{text}</span>;
};

const ProgBar = ({ label, note, pct, color, muted }) => (
  <div>
    <div className="mr-progbar-head">
      <span className="mr-progbar-label" style={muted ? { color: 'var(--raya-slate-400)' } : undefined}>{label}</span>
      <span className="mr-progbar-note" style={{ color: color === 'var(--raya-success)' ? 'var(--raya-success)' : undefined }}>{note}</span>
    </div>
    <div className="mr-progbar-track">
      {pct > 0 && <div className="mr-progbar-fill" style={{ width: pct + '%', background: color }} />}
    </div>
  </div>
);

const WhBar = ({ label, note, segs }) => (
  <div>
    <div className="mr-progbar-head">
      <span className="mr-progbar-label">{label}</span>
      <span className="mr-progbar-note">{note}</span>
    </div>
    <div className="mr-whbar-track">
      {segs.map((s, i) => <div key={i} style={{ width: s.w, background: s.c }} />)}
    </div>
  </div>
);

const StatusChip = ({ text, tone }) => {
  const t = TONES[tone];
  return <span className="mr-statuschip" style={{ color: t[0], background: t[1] }}>{text}</span>;
};

// ─── The seven module screens ────────────────────────────────────────────────
const InventoryScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">Stock summary — HDPE Granules, Grade A</span>
      <span className="mr-screen-meta">BATCH B-2261</span>
    </div>
    <div className="mr-stack mr-mb18">
      <WhBar label="Vatva WH-1" note="38 MT free · 8 MT reserved" segs={[{ w: '62%', c: '#2563eb' }, { w: '14%', c: '#93c5fd' }]} />
      <WhBar label="Vatva WH-2" note="31 MT free · 8 MT reserved" segs={[{ w: '51%', c: '#2563eb' }, { w: '13%', c: '#93c5fd' }]} />
      <WhBar label="Odhav Depot" note="17 MT free" segs={[{ w: '28%', c: '#2563eb' }]} />
    </div>
    <div className="mr-kpis mr-kpis-4 mr-mb16">
      <KpiTile label="Total free" value="86 MT" />
      <KpiTile label="Reserved" value="16 MT" />
      <KpiTile label="Below reorder" value="2 items" tone="warn" />
      <KpiTile label="Stock value" value="₹23.7L" />
    </div>
    <div className="mr-table">
      <div className="mr-thead" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span>Movement</span><span>Reference</span><span>Qty</span><span style={{ textAlign: 'right' }}>Balance</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">Allocated to sales order</span><span className="mr-mono">SO-1043</span>
        <span style={{ color: 'var(--raya-error)' }}>−16 MT</span><span style={{ textAlign: 'right' }}>86 MT</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">Goods receipt posted</span><span className="mr-mono">GRN-1187</span>
        <span style={{ color: 'var(--raya-success)' }}>+29.5 MT</span><span style={{ textAlign: 'right' }}>102 MT</span>
      </div>
    </div>
  </>
);

const ManufacturingScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">WO-2210 — 50 L HDPE Drum × 2,000</span>
      <span className="mr-screen-meta">LINE 2 · BLOW MOULDING</span>
    </div>
    <div className="mr-stack mr-mb18">
      <ProgBar label="Extrusion" note="Completed · 2,000 units" pct={100} color="var(--raya-success)" />
      <ProgBar label="Blow moulding" note="1,360 of 2,000 units" pct={68} color="#0891b2" />
      <ProgBar label="Trimming & finishing" note="In progress" pct={24} color="#22d3ee" />
      <ProgBar label="Packing" note="Not started" pct={0} muted />
    </div>
    <div className="mr-kpis mr-kpis-4 mr-mb16">
      <KpiTile label="Produced" value="1,360" />
      <KpiTile label="Scrap" value="1.2%" tone="warn" />
      <KpiTile label="Cost / unit" value="₹214" />
      <KpiTile label="On schedule" value="Yes" tone="ok" />
    </div>
    <div className="mr-table">
      <div className="mr-thead" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span>Component (BOM-77)</span><span>Required</span><span>Issued</span><span style={{ textAlign: 'right' }}>Status</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">HDPE Granules — Grade A</span><span>4.8 MT</span><span>4.8 MT</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Issued" tone="ok" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">Masterbatch — Blue</span><span>80 kg</span><span>80 kg</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Issued" tone="ok" /></span>
      </div>
    </div>
  </>
);

const PurchaseScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">PO-3391 — Gujarat Polychem</span>
      <span className="mr-screen-meta">NET 45 DAYS</span>
    </div>
    <div className="mr-kpis mr-kpis-3 mr-mb16">
      <div className="mr-kpi mr-kpi--outline"><div className="mr-kpi-label">PO value</div><div className="mr-kpi-value mr-kpi-value--sm">₹11.02L</div></div>
      <div className="mr-kpi mr-kpi--outline"><div className="mr-kpi-label">Received</div><div className="mr-kpi-value mr-kpi-value--sm">29.5 MT</div></div>
      <div className="mr-kpi" style={{ background: TONES.ok[1], borderColor: 'transparent' }}>
        <div className="mr-kpi-label" style={{ color: TONES.ok[0] }}>Three-way match</div>
        <div className="mr-kpi-value mr-kpi-value--sm" style={{ color: TONES.ok[0] }}>Passed</div>
      </div>
    </div>
    <div className="mr-table mr-mb16">
      <div className="mr-thead" style={{ gridTemplateColumns: '1.4fr 1fr 1.2fr 1fr' }}>
        <span>Document</span><span>Qty</span><span>Value</span><span style={{ textAlign: 'right' }}>Match</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.4fr 1fr 1.2fr 1fr' }}>
        <span className="mr-mono mr-b">PO-3391</span><span>30 MT</span><span>₹11,02,120</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Base" tone="slate" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.4fr 1fr 1.2fr 1fr' }}>
        <span className="mr-mono mr-b">GRN-1187</span><span>29.5 MT</span><span>₹10,84,180</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Matched" tone="ok" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.4fr 1fr 1.2fr 1fr' }}>
        <span className="mr-mono mr-b">VB-2094</span><span>29.5 MT</span><span>₹10,84,180</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Matched" tone="ok" /></span>
      </div>
    </div>
    <div className="mr-pillrow">
      <span className="mr-pill" style={{ color: TONES.warn[0], background: TONES.warn[1] }}>Short supply 0.5 MT flagged</span>
      <span className="mr-pill" style={{ color: TONES.info[0], background: TONES.info[1] }}>Payment scheduled 15 Sep</span>
      <span className="mr-pill" style={{ color: TONES.info[0], background: TONES.info[1] }}>ITC eligible</span>
    </div>
  </>
);

const CrmScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">Pipeline — this quarter</span>
      <span className="mr-screen-meta">₹48.2L IN PLAY</span>
    </div>
    <div className="mr-stack mr-stack--sm mr-mb18">
      {PIPELINE.map((p) => (
        <div className="mr-pipeline-row" key={p.stage}>
          <span className="mr-pipeline-stage">{p.stage}</span>
          <div className="mr-pipeline-track"><div className="mr-pipeline-fill" style={{ width: p.pct, background: p.color }} /></div>
          <span className="mr-pipeline-value">{p.value}</span>
        </div>
      ))}
    </div>
    <div className="mr-card mr-mb14">
      <div className="mr-card-head">
        <span className="mr-card-title">Campaign · Festive season offer</span>
        <span className="mr-pill" style={{ color: TONES.ok[0], background: TONES.ok[1] }}>Running</span>
      </div>
      <div className="mr-kpis mr-kpis-4">
        <div><div className="mr-stat-label">Sent</div><div className="mr-stat-value">2,400</div></div>
        <div><div className="mr-stat-label">Opened</div><div className="mr-stat-value">1,118</div></div>
        <div><div className="mr-stat-label">Enquiries</div><div className="mr-stat-value" style={{ color: 'var(--raya-blue-600)' }}>86</div></div>
        <div><div className="mr-stat-label">Orders won</div><div className="mr-stat-value" style={{ color: 'var(--raya-success)' }}>19</div></div>
      </div>
    </div>
    <div className="mr-stack mr-stack--xs">
      <div className="mr-taskrow">
        <span className="mr-taskrow-label">Follow up · Sunrise Polymers · quote expiring</span>
        <span style={{ color: 'var(--raya-slate-400)' }}>Today</span>
      </div>
      <div className="mr-taskrow">
        <span className="mr-taskrow-label">Quote sent · Kaveri Industries · 8 MT LDPE</span>
        <StatusChip text="₹2.1L" tone="info" />
      </div>
    </div>
  </>
);

const HrScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">People &amp; payroll — August 2026</span>
      <span className="mr-screen-meta">142 ON ROLL</span>
    </div>
    <div className="mr-kpis mr-kpis-4 mr-mb16">
      <KpiTile label="Present today" value={<span style={{ color: 'var(--raya-success)' }}>131</span>} />
      <KpiTile label="On leave" value={<span style={{ color: 'var(--raya-warning)' }}>11</span>} />
      <KpiTile label="Overtime hours" value="218" />
      <KpiTile label="Salary cost" value="₹41.6L" />
    </div>
    <div className="mr-card mr-mb14">
      <div className="mr-card-head mr-card-head--sm">
        <span className="mr-card-title">Payroll run</span>
        <span className="mr-pill" style={{ color: TONES.info[0], background: TONES.info[1] }}>Processing</span>
      </div>
      <div className="mr-progbar-track mr-mb8"><div className="mr-progbar-fill" style={{ width: '68%', background: 'var(--raya-blue-500)' }} /></div>
      <div className="mr-card-foot"><span>97 of 142 salary slips generated</span><span>PF · ESI · PT computed</span></div>
    </div>
    <div className="mr-table">
      <div className="mr-thead" style={{ gridTemplateColumns: '1.6fr 1.2fr 1fr 1fr' }}>
        <span>Request</span><span>Type</span><span>Days</span><span style={{ textAlign: 'right' }}>Action</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.6fr 1.2fr 1fr 1fr' }}>
        <span className="mr-b">R. Mehta · Line 2</span><span>Casual leave</span><span>2</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Approve" tone="ok" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.6fr 1.2fr 1fr 1fr' }}>
        <span className="mr-b">S. Patel · Stores</span><span>Shift swap</span><span>1</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Approve" tone="ok" /></span>
      </div>
    </div>
  </>
);

const FinanceScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">Finance overview — August 2026</span>
      <span className="mr-screen-meta">GSTR-1 / 3B READY</span>
    </div>
    <div className="mr-kpis mr-kpis-4 mr-mb16">
      <KpiTile label="Revenue" value="₹12.4L" sub={<SubBadge text="+18% MoM" tone="ok" />} />
      <KpiTile label="Receivables" value="₹8.9L" sub={<SubBadge text="₹1.4L overdue" tone="warn" />} />
      <KpiTile label="Payables" value="₹6.2L" />
      <KpiTile label="Gross margin" value="31.4%" />
    </div>
    <div className="mr-table mr-mb14">
      <div className="mr-thead" style={{ gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr' }}>
        <span>Voucher</span><span>Ledger</span><span>Amount</span><span style={{ textAlign: 'right' }}>Status</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr' }}>
        <span className="mr-b">Receipt RCP-0977 · NEFT</span><span>Sunrise Polymers</span>
        <span style={{ color: 'var(--raya-success)' }}>+₹5,68,760</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Reconciled" tone="ok" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr' }}>
        <span className="mr-b">Vendor bill VB-2094</span><span>Gujarat Polychem</span>
        <span style={{ color: 'var(--raya-error)' }}>−₹10,84,180</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Scheduled" tone="info" /></span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr' }}>
        <span className="mr-b">Payroll journal · August</span><span>Salaries &amp; wages</span>
        <span style={{ color: 'var(--raya-error)' }}>−₹41,62,400</span>
        <span style={{ textAlign: 'right' }}><StatusChip text="Draft" tone="slate" /></span>
      </div>
    </div>
    <div className="mr-pillrow">
      <span className="mr-pill" style={{ color: TONES.info[0], background: TONES.info[1] }}>Bank feed auto-matched</span>
      <span className="mr-pill" style={{ color: TONES.info[0], background: TONES.info[1] }}>Costing pulled from work orders</span>
    </div>
  </>
);

const ReportsScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">Sales overview — last 7 days</span>
      <span className="mr-screen-meta">LIVE DATA</span>
    </div>
    <div className="mr-kpis mr-kpis-3 mr-mb18">
      <KpiTile label="Orders" value="47" />
      <KpiTile label="Invoiced" value="₹12.4L" />
      <KpiTile label="Due today" value="6" tone="warn" />
    </div>
    <div className="mr-chart mr-mb14">
      {CHART_BARS.map((b) => (
        <div className="mr-chart-col" key={b.day}>
          <span className="mr-chart-val">{b.val}</span>
          <div className="mr-chart-bar" style={{ height: b.h, background: b.color }} />
          <span className="mr-chart-day">{b.day}</span>
        </div>
      ))}
    </div>
    <div className="mr-table">
      <div className="mr-thead" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span>Top item</span><span>Qty sold</span><span>Revenue</span><span style={{ textAlign: 'right' }}>Margin</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">HDPE Granules — Grade A</span><span>96 MT</span><span>₹6.8L</span><span style={{ textAlign: 'right' }}>28%</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <span className="mr-b">50 L HDPE Drum</span><span>1,120 units</span><span>₹3.1L</span><span style={{ textAlign: 'right' }}>36%</span>
      </div>
    </div>
  </>
);

// Sales by two-hour slot for the POS manager view, in ₹ thousands. Sums to the
// ₹1.84L "Sales today" tile (412 bills × ₹447).
const POS_HOURS = [['10', 12], ['12', 27], ['14', 23], ['16', 32], ['18', 50], ['20', 40]];
const POS_HOUR_MAX = Math.max(...POS_HOURS.map(([, v]) => v));

const PosScreen = () => (
  <>
    <div className="mr-screen-head">
      <span className="mr-screen-title">Counter sales — 3 stores, 7 counters</span>
      <span className="mr-screen-meta">5 SHIFTS OPEN</span>
    </div>
    <div className="mr-kpis mr-kpis-4 mr-mb16">
      <KpiTile label="Sales today" value="₹1.84L" />
      <KpiTile label="Bills" value="412" />
      <KpiTile label="Average bill" value="₹447" />
      <KpiTile label="Pending approvals" value="1" tone="warn" />
    </div>
    <div className="mr-table mr-mb16">
      <div className="mr-thead" style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 1fr' }}>
        <span>Store · counter</span><span>Cashier</span><span>Bills</span><span style={{ textAlign: 'right' }}>Sales</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 1fr' }}>
        <span className="mr-b">MG Road · Counter 1</span><span>Asha</span><span>96</span><span style={{ textAlign: 'right' }}>₹42,310</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 1fr' }}>
        <span className="mr-b">MG Road · Counter 2</span><span>Imran</span><span>88</span><span style={{ textAlign: 'right' }}>₹38,950</span>
      </div>
      <div className="mr-trow" style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 1fr' }}>
        <span className="mr-b">Satellite · Counter 1</span><span>Neha</span><span>71</span><span style={{ textAlign: 'right' }}>₹27,480</span>
      </div>
    </div>
    <div className="mr-chart">
      {POS_HOURS.map(([hour, v]) => (
        <div className="mr-chart-col" key={hour}>
          <span className="mr-chart-val">₹{v}k</span>
          <div className="mr-chart-bar" style={{ height: Math.round((v / POS_HOUR_MAX) * 54), flexShrink: 0, background: v === POS_HOUR_MAX ? '#ea580c' : '#fed7aa' }} />
          <span className="mr-chart-day">{hour}:00</span>
        </div>
      ))}
    </div>
  </>
);

const SCREENS = {
  inv: InventoryScreen,
  mfg: ManufacturingScreen,
  pur: PurchaseScreen,
  crm: CrmScreen,
  hr: HrScreen,
  fin: FinanceScreen,
  pos: PosScreen,
  rep: ReportsScreen,
};

const ModuleRiver = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [mod, setMod] = useState(0);

  // The site nav deep-links to a module as /#module-<key>: open that tab.
  useEffect(() => {
    const m = /^#module-([\w-]+)$/.exec(hash || '');
    if (!m) return;
    const i = MODULES_DEEP.findIndex((d) => d.key === m[1]);
    if (i >= 0) setMod(i);
  }, [hash]);
  const active = MODULES_DEEP[mod];
  const Screen = SCREENS[active.key];
  const badgeTone = TONES[active.badge[1]];

  return (
    <section id="explore" className="rl-section">
      <div className="rl-container rl-reveal">
        <div className="rl-head rl-head--wide">
          <div className="rl-eyebrow">— Inside the product</div>
          <h2 className="rl-h2">Pick a module. <em>See the real screen.</em></h2>
          <p className="rl-lead">Not feature bullets — the actual working views your team uses every day, with the data a real day looks like.</p>
        </div>
        <div className="mr-body">
          <div className="mr-tabs">
            {MODULES_DEEP.map((d, i) => {
              const open = i === mod;
              return (
                <button
                  type="button"
                  key={d.key}
                  id={`module-${d.key}`}
                  data-scroll-target="explore"
                  aria-expanded={open}
                  className={`mr-tab ${open ? 'is-open' : ''}`}
                  style={{ borderLeftColor: open ? d.color : 'var(--raya-slate-200)', background: open ? 'var(--raya-slate-50)' : 'transparent' }}
                  onClick={() => setMod(i)}
                >
                  <div className="mr-tab-head">
                    <div
                      className="mr-tab-icon"
                      style={{ background: open ? d.tint : 'var(--raya-slate-100)', color: open ? d.color : 'var(--raya-slate-500)' }}
                    >
                      <Icon name={d.icon} size={17} />
                    </div>
                    <span className="mr-tab-name" style={{ color: open ? 'var(--raya-slate-900)' : 'var(--raya-slate-600)' }}>{d.name}</span>
                  </div>
                  {open && (
                    <div className="mr-tab-detail">
                      <p className="mr-tab-desc">{d.desc}</p>
                      <div className="mr-tab-points">
                        {d.points.map((p) => (
                          <div className="mr-tab-point" key={p}><span className="mr-tab-check">✓</span>{p}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="rl-window-frame rl-window-frame--tall">
            <div className="rl-window">
              <div className="rl-window-bar">
                <span className="rl-window-crumb">{active.crumb}</span>
                <span className="rl-badge" style={{ color: badgeTone[0], background: badgeTone[1] }}>{active.badge[0]}</span>
              </div>
              <div className="mr-screen"><Screen /></div>
              {active.link && (
                <a
                  className="mr-screen-link"
                  href={active.link}
                  onClick={(e) => { e.preventDefault(); navigate(active.link); }}
                >
                  See {active.name} in detail <Icon name="arrow-right" size={14} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModuleRiver;
